import React, { useMemo, useState } from 'react';
import { Grid, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageContainer } from '../../components/ui/PageContainer';
import { useDevice } from '../../hooks/useDevice';
import { useGetUsersMe } from '../../api/generated/users/users';
import { GoalModal } from '../../components/goals/GoalModal';
import { useCreateGoal, useUserGoals } from '../../hooks/useGoalApi';
import { CreateGoalData, UpdateGoalData, User } from '../../types';
import { showToast } from '../../utils/toast';
import {
  useGetPerformanceReviewRoundsActive,
  useGetPerformanceReviewRoundsRoundIdFormsMine,
} from '../../api/generated/performance-review/performance-review';
import { PerformanceReviewRound } from '../../api/generated/model/performanceReviewRound';
import { PerformanceReviewForm } from '../../api/generated/model/performanceReviewForm';
import { useMyEssays } from '../../hooks/useEssayApi';
import { useMyQuarterlyReports } from '../../hooks/useQuarterlyApi';
import { useScoreTargets } from '../../hooks/useQuarterlyScoreApi';
import { monthLabel, reportingPeriod } from '../../components/essays/essayPeriod';
import { reportingQuarter } from '../../components/essays/quarterPeriod';
import { SubjectGoalsPanel } from '../performance-review/parts/SubjectGoalsPanel';
import { CompanyBlock } from './parts/CompanyBlock';
import { Hero } from './parts/Hero';
import { TeamsCompact } from './parts/TeamsCompact';
import { UpcomingBlock } from './parts/UpcomingBlock';
import { TodoList } from './parts/TodoList';
import { GradeCard } from './parts/GradeCard';
import { buildTodo, TodoInput } from './todo';
import { useCompanyModules } from '../../hooks/useCompanyModules';
import { PushOfferBanner } from '../../components/dashboard/PushOfferBanner';
import './dashboard.css';

/** Состояние документа для списка дел: возвращённый важнее несданного. */
const documentState = (doc?: {
  status?: string;
  returnedForRework?: unknown | null;
}): TodoInput['essay']['status'] => {
  if (!doc) return 'missing';
  if (doc.returnedForRework) return 'returned';
  return doc.status === 'submitted' ? 'submitted' : 'draft';
};

export const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const device = useDevice();
  const isMobile = device === 'mobile';
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ru';
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';

  const { data: me } = useGetUsersMe();
  const meUser = me as User | undefined;
  const meName = (meUser?.name || '').split(' ')[1] || meUser?.name || '';

  const essayPeriod = useMemo(() => reportingPeriod(), []);
  const quarter = useMemo(() => reportingQuarter(), []);

  // Разделы, не подключённые компании: ни запросов, ни дел, ни виджетов (`Tenant.modules`).
  const { hasModule } = useCompanyModules();
  const hasReview = hasModule('performanceReview');
  const hasGoals = hasModule('goals');

  const { data: essays } = useMyEssays(hasModule('essays'));
  const { data: reports } = useMyQuarterlyReports(hasModule('quarterly'));
  const { data: myGoals } = useUserGoals(meUser?._id || '', hasGoals);
  const { data: targets } = useScoreTargets(quarter.year, quarter.quarter, hasModule('quarterly'));

  const { data: active } = useGetPerformanceReviewRoundsActive({
    query: { enabled: hasReview, refetchInterval: 30_000, staleTime: 0 },
  });
  const activeRound = active as unknown as PerformanceReviewRound | undefined;
  const { data: myForms } = useGetPerformanceReviewRoundsRoundIdFormsMine(activeRound?._id || '', {
    query: { enabled: !!activeRound?._id, refetchInterval: 30_000, staleTime: 0 },
  });

  const [isCreateGoalOpen, setIsCreateGoalOpen] = useState(false);
  const createGoalMutation = useCreateGoal();

  const handleCreateGoal = async (data: CreateGoalData | UpdateGoalData) => {
    try {
      await createGoalMutation.mutateAsync(data as CreateGoalData);
      showToast({ title: t('messages.created', { item: t('goals.title') }), message: '' });
      setIsCreateGoalOpen(false);
    } catch {
      showToast({
        title: t('messages.error'),
        message: t('messages.errorSaving', { item: t('goals.title') }),
        type: 'error',
      });
    }
  };

  const items = useMemo(() => {
    const forms = (myForms as unknown as PerformanceReviewForm[]) ?? [];
    const selfForm = forms.find((f) => f.type === 'self');
    const essay = (essays || []).find(
      (e) => e.year === essayPeriod.year && e.month === essayPeriod.month,
    );
    const report = (reports || []).find(
      (r) => r.year === quarter.year && r.quarter === quarter.quarter,
    );
    const overdueGoals = (myGoals || []).filter((g) => g.isOverdue);

    const input: TodoInput = {
      essay: { period: essayPeriod, status: documentState(essay) },
      quarterly: { period: quarter, status: documentState(report) },
      scoring: {
        pending: (targets?.targets || []).filter((x) => x.hasReport && !x.myScore).length,
        lastDay: targets?.window.lastDay
          ? dayjs(targets.window.lastDay).locale(lang).format('D MMMM')
          : null,
        isWindowOpen: targets?.window.state === 'open',
      },
      review: {
        // Self-эссе просят только на своей стадии: на сборе обратной связи
        // его уже не примут, и звать человека туда незачем.
        selfPending:
          activeRound?.stage === 'collect-essay' && !!selfForm && selfForm.status !== 'submitted',
        peerPending:
          activeRound?.stage === 'collect-feedback'
            ? forms.filter((f) => f.type !== 'self' && f.status !== 'submitted').length
            : 0,
        stage: activeRound?.stage,
      },
      goals: { overdue: overdueGoals.length, firstTitle: overdueGoals[0]?.title },
    };

    return buildTodo(input, t, monthLabel(essayPeriod.year, essayPeriod.month, lang), hasModule);
  }, [
    myForms,
    essays,
    reports,
    targets,
    myGoals,
    activeRound,
    essayPeriod,
    quarter,
    lang,
    t,
    hasModule,
  ]);

  return (
    <PageContainer>
      <Stack gap="xl" maw={1200}>
        <Hero
          name={meName}
          locale={locale}
          todoCount={items.length}
          isMobile={isMobile}
          onSetGoal={hasGoals ? () => setIsCreateGoalOpen(true) : undefined}
        />

        <PushOfferBanner />

        {/* Две колонки: слева то, что требует действия, справа — про самого
            человека и его окружение. В одну колонку экран читался лентой, где
            грейд и команды весили столько же, сколько несданное эссе. */}
        <Grid gutter="xl">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="xl">
              <TodoList items={items} />
              {hasModule('calendar') && <UpcomingBlock />}
              <CompanyBlock roundId={activeRound?._id} roundStage={activeRound?.stage} />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="xl">
              {hasModule('competencyMap') && <GradeCard />}
              {meUser?._id && hasGoals && hasReview && (
                <SubjectGoalsPanel
                  userId={meUser._id}
                  hideWhenEmpty
                  heading={t('perfReview.goals.fromLastReview')}
                />
              )}
              <TeamsCompact meId={meUser?._id} />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <GoalModal
        opened={isCreateGoalOpen}
        onClose={() => setIsCreateGoalOpen(false)}
        onSubmit={handleCreateGoal}
        userId={meUser?._id || ''}
        isLoading={createGoalMutation.isPending}
      />
    </PageContainer>
  );
};
